<script>
import _ from 'lodash';
import { isAxiosError } from 'axios';
import * as XLSX from 'xlsx';
import { useConfigHelper } from '@ilteducation/config';
import useToast from '../../../composables/useToast';
import { useLocale } from '../../../composables/useLocale';
import { getPanelUrl } from '../../../helpers/urls';
import { useEditGroup, useGetOrganizationGroups, useGetUsers } from '@ilteducation/customer-mgmt';
import { useUserRoles } from '../../../composables/useUserRoles';
import { isClearSentinel } from './clearSentinel';
import {
  COLUMN_LABEL_KEYS,
  IDENTIFIER_FIELDS,
  IMPORT_COLUMNS,
  MANDATORY_FIELDS,
  isStudentRowWithGroups,
  resolveRole,
  useImportUsers,
} from './useImportUsers';

const panelUrl = getPanelUrl();

const countryFileMapper = {
  getExcelTemplate: (countryCode) => {
    switch (countryCode) {
      case 'de':
        return 'excel_upload_germany.xlsx';
      case 'fi':
        return 'excel_upload_finland.xlsx';
      case 'fr':
        return 'excel_upload_france.xlsx';
      case 'nl':
        return 'excel_upload_dutch.xlsx';
      case 'no':
        return 'excel_upload_norway.xlsx';
      case 'se':
        return 'excel_upload_sweden.xlsx';
      case 'uk':
      case 'us':
      default:
        return 'excel_upload_english.xlsx';
    }
  },
  getExcelGuides: (countryCode) => {
    const englishGuidePdf = `${panelUrl}/pdf/user_import_eng.pdf`;
    switch (countryCode) {
      case 'de':
        return { native: `${panelUrl}/pdf/user_import_de.pdf`, translated: englishGuidePdf };
      case 'fi':
        return { native: `${panelUrl}/pdf/user_import_fi.pdf`, translated: englishGuidePdf };
      case 'fr':
        return { native: `${panelUrl}/pdf/user_import_fr.pdf`, translated: englishGuidePdf };
      case 'no':
        return { native: `${panelUrl}/pdf/user_import_no.pdf`, translated: englishGuidePdf };
      case 'se':
        return {
          native: 'https://files.stonly.com/stepAttachment/481d1bd9-2d2f-48c7-97b7-f044c5a15978.pdf',
          translated: 'https://files.stonly.com/stepAttachment/3383341a-da89-4b94-9a89-1102e13ec9f2.pdf',
        };
      case 'uk':
      case 'us':
        return { native: englishGuidePdf, translated: englishGuidePdf };
      default:
        return { native: null, translated: null };
    }
  },
};

const realm = useConfigHelper().getRealm();
const excelGuides = countryFileMapper.getExcelGuides(realm);
const downloadFiles = {
  excelTemplate: `${panelUrl}/templates/${countryFileMapper.getExcelTemplate(realm)}`,
  excelGuideNative: excelGuides.native,
  excelEnglishGuideNative: excelGuides.translated,
};

export default {
  name: 'ImportUsers',
  layout: 'controlpanel',
  middleware: ['auth'],
  props: {
    organization: {
      type: Object,
      required: true,
    },
    onImportStatusChange: {
      type: Function,
      required: true,
    },
  },

  setup() {
    const { toast } = useToast();
    const { t } = useLocale();

    const { fetchAllGroups } = useGetOrganizationGroups();
    const { createGroup } = useEditGroup();
    const { getUsers, fetchAllUsers } = useGetUsers();
    const { getRolesPossibleToCreateWith } = useUserRoles();
    const { createBatchUsers, updateExistingUsers, removeUsersFromOrganization, fetchMembershipsByUser } =
      useImportUsers();

    return {
      toast,
      $t: t,
      fetchAllGroups,
      createGroupApi: createGroup,
      createBatchUsers,
      getUsers,
      fetchAllUsers,
      getRolesPossibleToCreateWith,
      updateExistingUsers,
      removeUsersFromOrganization,
      fetchMembershipsByUser,
    };
  },

  data() {
    return {
      // Column index -> field name, derived from the shared column definition so the two cannot
      // drift apart.
      required: Object.fromEntries(IMPORT_COLUMNS.map((column, index) => [index, column.field])),
      downloadFiles,
      currentSheet: null,
      // Which of the `required` fields the uploaded file's header row actually covered, in template
      // order - the error tables show one column per column of the file.
      sheetFields: [],
      errorRows: [],
      orgStudents: [],
      orgEmployees: [],
      orgGuardians: [],
      finished: 0,
      errorUsers: [],
      validated: false,
      importing: false,
      deleteStudents: false,
      deleteEmployees: false,
      deleteGuardians: false,
      importRequests: null,
      deleteRequests: 0,
      madeAdditionalDeletion: false,
      uploadText: '',
      processText: null,
      allDone: false,
      excelRight: false,
      usersWithImportErrors: [],
      orgName: 'test',
      progressText: '',
      importJobs: [],
      usersToImport: {},
      forceRecomputeCounter: 0,
      usersToDelete: [],
      deletionJobs: [],
      deleteRequestsDone: 0,
      deleting: false,
      creatingGroup: false,
      numberOfCreatedGroupJobs: 0,
      createdGroupCount: 0,
      canRemoveGuardians: false,
      canRemoveStudents: false,
      canRemoveEmployees: false,
      allowedRoles: [],
      disallowedRoleRowCount: 0,
      disallowedRoleWordsInFile: [],
      isLoading: false,
      file: null,
    };
  },
  computed: {
    organizationTypeLabel() {
      const key = `lcp.organization_type.${this.$props.organization.type}`;
      const label = this.$t(key);
      return label === key ? this.$props.organization.type : label;
    },
    progress() {
      if (this.deleting) {
        return this.deleteRequests != null
          ? this.deleteRequestsDone / this.deleteRequests
          : this.deleteRequestsDone / 50;
      }

      if (this.creatingGroup) {
        return this.createdGroupCount / this.numberOfCreatedGroupJobs;
      }

      return this.importRequests != null ? this.finished / this.importRequests : this.finished / 50;
    },
    getImportedUsers() {
      const output = [];

      const entries = Object.entries(this.usersToImport);
      for (const i in entries) {
        const user = entries[i][1];

        if (user.status === 'created' || user.status === 'updated') {
          output.push(user);
        }
      }
      return output;
    },
    getUsersToImport() {
      const output = [];

      const entries = Object.entries(this.usersToImport);
      for (const i in entries) {
        const user = entries[i][1];

        if (user.status === 'not-imported') {
          output.push(user);
        }
      }
      return output;
    },
    getUsersWithErrors() {
      const output = [];

      const entries = Object.entries(this.usersToImport);

      for (const i in entries) {
        const user = entries[i][1];

        if (user.status === 'error') {
          output.push(user);
        }
      }

      return output;
    },
    doDeletion() {
      return this.deleteStudents || this.deleteEmployees || this.deleteGuardians;
    },
    // One column per column of the uploaded file, so an error on any field - including the
    // identifier columns - can be shown against the field it concerns. The mandatory fields are
    // always kept: checkThatMandatoryFieldsExist flags them whether or not the file's header row
    // covers them, and without a column of their own that error would have nowhere to appear.
    // Ordering follows `required`, which is the template's column order.
    importErrorColumnFields() {
      return Object.values(this.required).filter(
        (field) => MANDATORY_FIELDS.includes(field) || this.sheetFields.includes(field),
      );
    },
    importErrorHeaders() {
      return [
        { title: this.$t('row'), key: 'row' },
        ...this.importErrorColumnFields.map((field) => ({ title: this.getTypeName(field), key: field })),
      ];
    },
    // Slot names cannot be built inline in the template, so they are precomputed here.
    importErrorColumns() {
      return this.importErrorColumnFields.map((field) => ({ field, slot: `item.${field}` }));
    },
    getBluePrint() {
      return {
        translations: {
          student: this.$t('lcp.import_users.student'),
          employee: this.$t('lcp.import_users.employee'),
          guardian: this.$t('lcp.import_users.guardian'),
          clear: this.$t('lcp.import_users.clear'),
        },
        users: [],
      };
    },
  },
  methods: {
    loaderShow() {
      this.isLoading = true;
    },
    loaderHide() {
      this.isLoading = false;
    },
    noticeShow(data) {
      this.toast(data.message, data.type);
    },
    clearUserState() {},
    async fetchGroups(organizationId) {
      return await this.fetchAllGroups(organizationId);
    },

    complete(val) {
      console.debug('all done!');
      if (val) {
        this.noticeShow({
          type: 'success',
          message: this.$t('lcp.import_users.upload_summery', {
            users: this.getImportedUsers.length,
            errors: this.getUsersWithErrors.length,
          }),
        });

        this.file = null;
        this.clearUserState();
      }

      this.importComplete();
    },

    createGroup(data) {
      return this.createGroupApi(data).then(() => {
        return true;
      });
    },

    /**
     * @returns GetOrganizationResponse
     */
    fetchOrganization(_organizationId) {
      return this.organization;
    },

    async retryRequest(request, numberOfReties = 5) {
      let lastError;
      for (let i = 0; i < numberOfReties; i++) {
        try {
          const response = await request();
          return response;
        } catch (error) {
          lastError = error;
        }
      }
      throw lastError;
    },

    async createGroupsByJobs(jobs) {
      this.creatingGroup = true;

      this.progressText = this.$t('lcp.import_users.fetching_groups');

      const allGroups = jobs.flatMap((j) =>
        j.users.flatMap((u) => {
          if (u.group && u.group.includes('|')) {
            return u.group.split('|').map((g) => g.trim());
          }
          return u.group;
        }),
      );

      const uniqueGroups = [...new Set(allGroups)];

      const existGroups = await this.retryRequest(() => {
        return this.fetchGroups(this.$props.organization.uid);
      });
      const existGroupByName = new Map(existGroups.map((g) => [g.name.toLowerCase(), g]));

      const missingGroups = uniqueGroups
        .filter((gName) => !!gName)
        .filter((gName) => !existGroupByName.has(gName.toLowerCase()));

      const allMissingGroups = [...missingGroups];
      this.progressText = this.$t('lcp.import_users.creating_groups');
      this.numberOfCreatedGroupJobs = uniqueGroups.length;
      this.createdGroupCount = uniqueGroups.length - missingGroups.length;

      while (missingGroups.length > 0) {
        const chunkGroupNames = missingGroups.splice(0, 10);

        await Promise.all(
          chunkGroupNames.map((gName) =>
            this.retryRequest(() =>
              this.createGroup({
                generateCode: false,
                name: gName,
                schoolUnitId: this.$props.organization.uid,
              }),
            ),
          ),
        );
        this.createdGroupCount += chunkGroupNames.length;
      }
      this.progressText = this.$t('lcp.import_users.groups_created');
      this.creatingGroup = false;

      return allMissingGroups;
    },

    async waitTillGroupsAppears(groups, msCounter = 0) {
      if (!groups.length || msCounter > 60000) {
        return [];
      }

      const sleep = (time = 10000) =>
        new Promise((resolve) => {
          setTimeout(resolve, time);
        });

      await sleep();

      const existGroups = await this.fetchGroups(this.$props.organization.uid);
      const existGroupByName = new Map(existGroups.map((g) => [g.name.toLowerCase(), g]));

      const missingGroups = groups
        .filter((gName) => !!gName)
        .filter((gName) => !existGroupByName.has(gName.toLowerCase()));

      return this.waitTillGroupsAppears(missingGroups, msCounter + 10000);
    },

    importStart() {
      this.importing = true;
      this.onImportStatusChange(this.importing);
    },
    importComplete() {
      this.importing = false;
      this.onImportStatusChange(this.importing);
    },

    async startImporting() {
      this.importStart();

      this.allowedRoles = await this.getRolesPossibleToCreateWith(this.$props.organization);

      const groups = await this.createGroupsByJobs(this.importJobs);
      await this.waitTillGroupsAppears(groups);

      const allGroups = await this.fetchAllGroups(this.$props.organization.uid);
      const groupsByName = new Map(allGroups.map((g) => [g.name.toLowerCase(), g.uid]));

      const jobs = this.importJobs;
      this.progressText = this.$t('lcp.import_users.importing_users');

      try {
        while (jobs.length > 0) {
          const chunk = jobs.splice(0, 10);

          await Promise.all(
            chunk.map((j) => {
              const request = () => this.importUsers(j, groupsByName);
              return this.retryRequest(request);
            }),
          );
        }
      } catch {
        this.noticeShow({
          type: 'error',
          message: this.$t('something_went_wrong'),
        });
        this.resetData();
      }

      this.progressText = this.$t('lcp.import_users.users_imported');

      this.validated = false;
      if (this.doDeletion && this.getImportedUsers.length > 0) {
        this.prepareDeletion();
        return;
      }
      this.allDone = true;
      this.complete(this.allDone);
    },

    async importUsers(userBatch, groupsByName) {
      console.debug('start importing');
      const organizationUid = this.$props.organization.uid;

      // find existing user by userNames in current organization. Deactivated users are only
      // returned when asked for explicitly, and only to administrators - for anyone else the
      // second response is the same active users as the first one.
      const usernames = userBatch.users.map((u) => u.username);
      const [activeUsersResponse, deactivatedUsersResponse] = await Promise.all([
        this.getUsers({ filters: { usernames, schoolUnits: [organizationUid] } }),
        this.getUsers({ filters: { usernames, schoolUnits: [organizationUid], active: false } }),
      ]);

      const existingUsers = [...(activeUsersResponse?.data || []), ...(deactivatedUsersResponse?.data || [])];
      const existingUserMap = new Map(existingUsers.map((u) => [u.userName.toLowerCase(), u]));

      const notInOrganization = userBatch.users.filter((u) => !existingUserMap.has(u.username.toLowerCase()));
      const existingUsersToUpdate = userBatch.users.filter((u) => existingUserMap.has(u.username.toLowerCase()));

      if (notInOrganization.length > 0) {
        const usersToUpsertBatch = {
          ...userBatch,
          users: notInOrganization,
        };

        try {
          const response = await this.createBatchUsers(
            organizationUid,
            usersToUpsertBatch,
            groupsByName,
            this.allowedRoles,
          );
          this.finished += notInOrganization.length;

          for (const [username, value] of Object.entries(response.data)) {
            const lowerUsername = username.toLowerCase();
            if (this.usersToImport[lowerUsername]) {
              this.usersToImport[lowerUsername].status = value.status;
              if (value.status === 'error') {
                this.usersToImport[lowerUsername].errors = value.errors;
              }
            }
          }
        } catch (error) {
          console.error('Error creating batch users:', error);
          notInOrganization.forEach((u) => {
            const lowerUsername = u.username.toLowerCase();
            if (this.usersToImport[lowerUsername]) {
              this.usersToImport[lowerUsername].status = 'error';
              this.usersToImport[lowerUsername].errors = error;
            }
          });
        }
      }

      if (existingUsersToUpdate.length > 0) {
        try {
          const studentUidsWithGroups = existingUsersToUpdate
            .filter((u) => isStudentRowWithGroups(u, userBatch.translations))
            .map((u) => existingUserMap.get(u.username.toLowerCase())?.uid)
            .filter((uid) => !!uid);

          const membershipsByUser = await this.fetchMembershipsByUser(studentUidsWithGroups, organizationUid);

          const updatedUsers = await this.updateExistingUsers(
            existingUsersToUpdate,
            existingUsers,
            organizationUid,
            groupsByName,
            userBatch.translations,
            this.allowedRoles,
            membershipsByUser,
          );
          this.finished += updatedUsers.length;

          updatedUsers.forEach((user) => {
            const lowerUsername = user.username.toLowerCase();
            if (this.usersToImport[lowerUsername]) {
              this.usersToImport[lowerUsername].status = user.status;
              if (user.status === 'error') {
                this.usersToImport[lowerUsername].errors = user.errors;
              }
            }
          });
        } catch (error) {
          console.error('Error updating existing users:', error);
          existingUsersToUpdate.forEach((u) => {
            const lowerUsername = u.username.toLowerCase();
            if (this.usersToImport[lowerUsername]) {
              this.usersToImport[lowerUsername].status = 'error';
              this.usersToImport[lowerUsername].errors = error;
            }
          });
        }
      }

      this.forceRecomputeCounter++;
    },
    handleFileChange(file) {
      this.resetData();
      console.debug(file);

      if (file === null || typeof file === 'undefined') {
        return;
      }

      if (file.name.indexOf('.xlsx') === -1) {
        this.noticeShow({
          type: 'error',
          message: this.$t('lcp.import_users.file_type_is_invalid'),
        });
        this.forceRecomputeCounter += 1;
        return;
      }

      const rABS = true;
      const reader = new FileReader();
      reader.onload = (e) => {
        let data = e.target.result;
        if (!rABS) data = new Uint8Array(data);
        const workbook = XLSX.read(data, { type: rABS ? 'binary' : 'array' });

        const json = XLSX.utils.sheet_to_json(workbook.Sheets[Object.keys(workbook.Sheets)[0]], {
          header: 1,
          blankrows: true,
        });

        const headerNames = json[0];
        // Header columns beyond the ones we know how to map are ignored, same as when reading rows.
        this.sheetFields = Object.keys(headerNames ?? {})
          .map((column) => this.required[column])
          .filter((field) => !!field);
        const out = [];

        const safeMap = (value, field) => {
          if (field === 'username') {
            return _.isUndefined(value) ? value : value.toString().trim().toLowerCase();
          }
          return _.isUndefined(value) ? value : value.toString().trim();
        };

        for (const key in json) {
          if (key > 0) {
            if (json[key].length) {
              const row = {};
              for (const key2 in headerNames) {
                const value = json[key][key2];

                Object.assign(row, {
                  [this.required[key2]]: safeMap(value, this.required[key2]),
                });
              }

              if (Object.keys(row).every((attr) => !row[attr])) {
                // skip rows with only falsy values
                continue;
              }

              Object.assign(row, { row: parseInt(key, 10) });
              Object.assign(row, { status: 'not-imported' });

              out.push(row);
            }
          }
        }

        this.currentSheet = out;
        this.importRequests = this.currentSheet.length;

        // Counted against the same list the import itself gates on, so the warning matches what
        // will actually fail. An empty list means it has not loaded yet, so warn about nothing.
        const disallowedRows = this.allowedRoles.length
          ? out.filter((row) => {
              const role = resolveRole(row.type || '', this.getBluePrint.translations);
              return !!role && !this.allowedRoles.includes(role);
            })
          : [];
        this.disallowedRoleRowCount = disallowedRows.length;
        // The words as the file spells them: any template's language is accepted, so translating
        // them into the interface language would send the uploader looking for the wrong word.
        const wordsSeen = new Map();
        for (const row of disallowedRows) {
          const word = (row.type || '').trim();
          if (word && !wordsSeen.has(word.toLowerCase())) {
            wordsSeen.set(word.toLowerCase(), word);
          }
        }
        this.disallowedRoleWordsInFile = [...wordsSeen.values()];

        if (this.currentSheet.length < 1) {
          this.noticeShow({
            type: 'error',
            message: this.$t('lcp.import_users.file_is_empty'),
          });
          this.currentSheet = null;
          return;
        }

        if (this.checkThatMandatoryFieldsExist(out) === false) {
          this.noticeShow({
            type: 'error',
            message: this.$t('lcp.import_users.change_rows_and_import'),
          });
          return;
        }
        this.validated = true;
        this.prepareImport();
      };

      if (rABS) reader.readAsBinaryString(file);
      else reader.readAsArrayBuffer(file);
    },
    prepareImport() {
      this.cleanSheet();
      console.debug('prepare Import');

      const sortedUsers = [...this.currentSheet].sort((a, b) => a.type.localeCompare(b.type));
      const chunkedUsers = this.chunkUsers(sortedUsers);

      for (const i in chunkedUsers) {
        const bluePrint = JSON.parse(JSON.stringify(this.getBluePrint));
        for (const i2 in chunkedUsers[i]) {
          bluePrint.users.push(chunkedUsers[i][i2]);
        }
        this.importJobs.push(bluePrint);
      }
    },
    async prepareDeletion() {
      console.debug('prepare deletion');
      this.importing = true;
      this.deleting = true;
      this.progressText = this.$t('lcp.import_users.fetching_existing_users');

      try {
        const orgUid = this.$props.organization.uid;

        const allOrgUsers = await this.fetchAllUsers({
          filters: {
            schoolUnits: [orgUid],
          },
        });

        const employees = this.deleteEmployees
          ? allOrgUsers.filter((u) =>
              u.associations.some(
                (a) => a.schoolUnit.uid === orgUid && a.role === 'employee' && a.isAdministrator === false,
              ),
            )
          : [];
        const students = this.deleteStudents
          ? allOrgUsers.filter((u) => u.associations.some((a) => a.schoolUnit.uid === orgUid && a.role === 'student'))
          : [];
        const guardians = this.deleteGuardians
          ? allOrgUsers.filter((u) => u.associations.some((a) => a.schoolUnit.uid === orgUid && a.role === 'guardian'))
          : [];

        const excelUsers = this.currentSheet.map((u) => ({ ...u, username: (u.username || '').toLowerCase().trim() }));

        const mapUser = (u) => ({ ...u, username: (u.userName || '').toLowerCase().trim() });
        const diffEmployees = _.differenceBy(employees.map(mapUser), excelUsers, 'username');
        const diffStudents = _.differenceBy(students.map(mapUser), excelUsers, 'username');
        const diffGuardians = _.differenceBy(guardians.map(mapUser), excelUsers, 'username');

        let joined = _.concat(diffEmployees, diffStudents, diffGuardians);

        this.usersToDelete = joined;
        this.deleteRequests = joined.length;

        if (this.usersToDelete.length === 0) {
          this.importing = false;
          this.deleting = false;
          this.allDone = true;
          this.progressText = 'users_are_deleted';
          return;
        }

        this.progressText = this.$t('lcp.import_users.removing_users');
        await this.deleteUsers();
      } catch (error) {
        console.error('Deletion preparation failed:', error);
        this.noticeShow({
          type: 'error',
          message: this.$t('something_went_wrong'),
        });
        this.resetData();
      }
    },
    async deleteUsers() {
      console.debug('delete users');
      this.importing = true;
      this.deleting = true;

      const userUids = this.usersToDelete.map((u) => u.uid);
      try {
        await this.removeUsersFromOrganization(this.$props.organization.uid, userUids);
        this.deleteRequestsDone = userUids.length;
        this.importing = false;
        this.deleting = false;
        this.allDone = true;
        this.progressText = 'users_are_deleted';
        this.complete(true);
      } catch (error) {
        console.error('Batch deletion failed:', error);
        this.noticeShow({
          type: 'error',
          message: this.$t('something_went_wrong'),
        });
      }
    },
    cleanSheet() {
      console.debug('clean sheet');
      for (const i in this.currentSheet) {
        this.usersToImport[this.currentSheet[i].username.toLowerCase()] = this.currentSheet[i];
        this.currentSheet[i] = _.each(this.currentSheet[i], (val, key) => {
          if (val == null || _.isUndefined(val)) {
            delete this.currentSheet[i][key];
          }
        });
      }
      this.forceRecomputeCounter++;
    },
    resetData() {
      console.debug('resetting data');
      this.allDone = false;
      this.currentSheet = null;
      this.sheetFields = [];

      this.deleteRequests = 0;
      this.deleteRequestsDone = 0;
      this.deleting = false;
      this.madeAdditionalDeletion = false;

      this.errorRows = [];
      this.errorUsers = [];
      this.usersWithImportErrors = [];

      this.forceRecomputeCounter = 0;

      this.finished = 0;

      this.importing = false;
      this.importJobs = [];
      this.importRequests = 0;

      this.validated = false;

      this.progressText = '';
      this.usersToImport = {};
      this.usersToDelete = [];
      this.disallowedRoleRowCount = 0;
      this.disallowedRoleWordsInFile = [];

      this.uploadText = '';
      this.processText = null;

      this.orgStudents = [];
      this.orgEmployees = [];
      this.orgGuardians = [];
    },
    chunkUsers(users) {
      console.debug('chunk Users');
      let i;
      let j;
      const chunk = 10;
      const chunkedUsers = [];
      for (i = 0, j = users.length; i < j; i += chunk) {
        chunkedUsers.push(users.slice(i, i + chunk));
      }
      return chunkedUsers;
    },
    checkThatMandatoryFieldsExist(users) {
      console.debug('check mandatory fields');

      for (const i in users) {
        const user = users[i];
        let userHasAllMandatoryFields = true;
        for (const field of MANDATORY_FIELDS) {
          if (user[field] === undefined || user[field] === '') {
            userHasAllMandatoryFields = false;
            if (user.missingFields === undefined) user.missingFields = [];
            user.missingFields.push(field);
          } else if (field === 'username') {
            if (this.checkIfValidUsername(user.username) === false) {
              user.invalidUsername = true;
              userHasAllMandatoryFields = false;
            }
          }
        }
        if (userHasAllMandatoryFields === false) {
          this.errorRows.push(user);
        }
      }

      this.flagDuplicateUsernames(users);
      this.flagDuplicateIdentifiers(users);

      return this.errorRows.length <= 0;
    },
    // Groups rows by the value of one column, trimmed and lower-cased so that rows differing only in
    // casing or padding land in the same group. That matches what userville does to an identifier
    // before storing it (src/formatters/user.ts lower-cases and trims the value), which is why two
    // rows differing only in case really do collide on its (realm, value, type) key.
    //
    // It is not a full equivalence: userville does nothing beyond case and whitespace, so values
    // that differ in formatting - `070-123 45 67` against `0701234567` - are genuinely two distinct
    // identifiers there, and are correctly left ungrouped here.
    groupRowsByNormalizedValue(users, field) {
      const rowsByValue = new Map();

      for (const user of users) {
        const value = user[field];
        if (value === undefined || value === null || `${value}`.trim() === '') {
          continue;
        }
        const key = `${value}`.trim().toLowerCase();
        if (!rowsByValue.has(key)) {
          rowsByValue.set(key, []);
        }
        rowsByValue.get(key).push(user);
      }

      return rowsByValue;
    },
    flagDuplicateUsernames(users) {
      console.debug('check duplicate usernames');

      // Two rows sharing a username would otherwise race to create the same account on import,
      // with whichever request wins decided by network timing rather than the file's content.
      const errorRowSet = new Set(this.errorRows);
      for (const rows of this.groupRowsByNormalizedValue(users, 'username').values()) {
        if (rows.length <= 1) {
          continue;
        }
        for (const user of rows) {
          user.duplicateUsername = true;
          if (!errorRowSet.has(user)) {
            errorRowSet.add(user);
            this.errorRows.push(user);
          }
        }
      }
    },
    flagDuplicateIdentifiers(users) {
      console.debug('check duplicate identifiers');

      // A cleared cell carries no identifier, so any number of rows may say it.
      const carriesIdentifier = (user, field) => !isClearSentinel(user[field], this.getBluePrint.translations);

      // Same race as duplicate usernames, one level down: identifiers are unique per realm, so two
      // rows sharing one collide in the database and only one of them can win. Each identifier type
      // is checked on its own - the same value under two different types is not a conflict.
      //
      // Scope: this only compares rows of the uploaded file against each other. An identifier that
      // already belongs to another user in the realm still collides at import time and surfaces as
      // a backend error, because there is no bulk pre-flight for identifiers - /users/exists takes
      // usernames only, and the get-users identifier filter accepts one identifier per request and
      // is scoped to the caller's own schools while the constraint is realm-wide.
      const errorRowSet = new Set(this.errorRows);
      for (const field of IDENTIFIER_FIELDS) {
        const rowsWithIdentifier = users.filter((user) => carriesIdentifier(user, field));
        for (const rows of this.groupRowsByNormalizedValue(rowsWithIdentifier, field).values()) {
          if (rows.length <= 1) {
            continue;
          }
          for (const user of rows) {
            if (user.duplicateIdentifiers === undefined) {
              user.duplicateIdentifiers = [];
            }
            if (!user.duplicateIdentifiers.includes(field)) {
              user.duplicateIdentifiers.push(field);
            }
            if (!errorRowSet.has(user)) {
              errorRowSet.add(user);
              this.errorRows.push(user);
            }
          }
        }
      }
    },
    checkIfValidUsername(username) {
      const regexPattern = new RegExp(/^([A-Za-z0-9@_%#*.,! -]){1,255}$/);
      return regexPattern.test(username);
    },
    // Pre-import validation errors (missing/invalid/duplicate) are per-field, so the message for
    // a given column only ever concerns that column - a row can still have several, one per field.
    getFieldError(user, field) {
      if (field === 'username') {
        if (user.invalidUsername) return this.$t('validation.value_invalid');
        if (user.duplicateUsername) return this.$t('lcp.import_users.duplicate_username');
      }
      if (user.duplicateIdentifiers?.includes(field)) {
        return this.$t('lcp.import_users.duplicate_identifier');
      }
      if (user.missingFields?.includes(field)) {
        return this.$t('validation.value_required');
      }
      return null;
    },
    getFieldDisplay(user, field) {
      const error = this.getFieldError(user, field);
      return error ? `${user[field] || ''} — ${error}` : user[field] || '';
    },
    // Post-import errors come back from the API keyed by attribute, so map each one to the column
    // it concerns; errors with no attribute (eg. forbidden) are shown on the row column instead.
    getFieldApiError(user, field) {
      const error = user.errors;
      if (!error) return null;
      if (error.error === 'validation_error') {
        const match = error.validation_errors?.find((e) => e.attribute === field);
        return match ? this.getReasonName(match.reason) : null;
      }
      if (error.error === 'invalid_input' && error.input_errors?.attribute === field) {
        return this.getReasonName(error.input_errors.reason);
      }
      if (error.error === 'role_not_permitted' && field === 'type') {
        return `${this.$t(`lcp.role_${error.role}`)}: ${this.$t('lcp.permissions.not_permitted')}`;
      }
      return null;
    },
    getFieldApiDisplay(user, field) {
      const error = this.getFieldApiError(user, field);
      return error ? `${user[field] || ''} — ${error}` : user[field] || '';
    },
    // Catches everything getFieldApiError can't attribute to a column: attributes with no column
    // of their own (eppn, gsuiteemail, ...), and the unshaped errors useImportUsers also produces
    // (raw axios response data, a bare Error, or the plain 'User not found...' string) - so a row
    // is never left with a red X and no explanation at all.
    getRowLevelApiError(user) {
      const error = user.errors;
      if (!error) return null;

      const columnFields = this.importErrorColumnFields;

      if (error.error === 'forbidden') {
        return this.$t('lcp.import_users.import_forbidden');
      }
      if (error.error === 'role_not_permitted') {
        return null; // already shown under the type column
      }
      if (error.error === 'validation_error') {
        const unmatched = (error.validation_errors || []).filter((e) => !columnFields.includes(e.attribute));
        if (unmatched.length === 0) return null;
        return unmatched.map((e) => `${this.getTypeName(e.attribute)}: ${this.getReasonName(e.reason)}`).join(', ');
      }
      if (error.error === 'invalid_input') {
        if (columnFields.includes(error.input_errors?.attribute)) return null;
        return `${this.getTypeName(error.input_errors?.attribute)}: ${this.getReasonName(error.input_errors?.reason)}`;
      }

      return this.$t('something_went_wrong');
    },
    getReasonName(e) {
      let n = e;
      switch (n) {
        case 'max-length':
          n = this.$t('validation.too_many_characters');
          break;
        case 'min-length':
          n = this.$t('validation.too_few_characters');
          break;
        case 'missing':
          n = this.$t('validation.required');
          break;
        case 'invalid':
          n = this.$t('validation.wrong_format');
          break;
        case 'characters':
          n = this.$t('validation.value_invalid');
          break;
        case 'type must be provided, but does not exist':
          n = this.$t('validation.type_invalid');
          break;
        case 'taken':
          n = this.$t('validation.taken');
          break;
        case 'the existing user is not of the same type as the given one':
          n = this.$t('validation.user_exists_as_other_type');
          break;
        default:
          n = this.$t('validation.unknown');
          break;
      }
      return n;
    },
    getTypeName(e) {
      // Sheet columns carry their own label key, so adding a column to IMPORT_COLUMNS cannot leave
      // it titled "unknown". The cases below are for attribute names the backend uses that are not
      // columns of the sheet.
      const columnLabelKey = COLUMN_LABEL_KEYS.get(e);
      if (columnLabelKey) {
        return this.$t(columnLabelKey);
      }

      let n = e;
      switch (n) {
        case 'userName':
          n = this.$t('account.username');
          break;
        case 'office365id':
          n = this.$t('account.office365');
          break;
        case 'bankId':
          n = this.$t('account.bankid');
          break;
        default:
          n = this.$t('validation.unknown');
          break;
      }
      return n;
    },
  },
  watch: {
    usersToImport: {
      deep: true,
      handler() {
        this.forceRecomputeCounter += 1;
      },
    },
  },
  async mounted() {
    this.loaderShow();

    try {
      const organization = this.fetchOrganization(this.$props.organization.legacyId);

      this.orgName = organization.name;
      this.canRemoveGuardians = true;
      this.canRemoveStudents = true;
      this.canRemoveEmployees = true;
      this.excelRight = true;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.status === 403) {
          this.excelRight = false;
          this.noticeShow({
            type: 'error',
            message: this.$t(`lcp.import_users.import_forbidden`),
          });
        }
      }
    } finally {
      this.loaderHide();
    }
  },
};
</script>

<template>
  <v-container>
    <v-row v-if="isLoading">
      <v-col>
        <v-progress-linear indeterminate></v-progress-linear>
      </v-col>
    </v-row>
    <v-row v-if="!isLoading && !excelRight">
      <v-col>
        <v-alert color="warning" variant="outlined" :text="$t('lcp.import_users.no_excell_import_right')"></v-alert>
      </v-col>
    </v-row>

    <v-row v-if="importing && madeAdditionalDeletion === false">
      <v-col>
        <div class="subtitle-1">{{ processText }}</div>
      </v-col>
    </v-row>

    <v-row v-if="!importing && excelRight">
      <v-col>
        <v-row>
          <v-col>
            <h2>{{ orgName }}</h2>
          </v-col>
        </v-row>

        <v-row v-if="allDone" class="text-black">
          <v-col>
            <v-icon color="success">mdi-check</v-icon>
            {{ getImportedUsers.length }}
            {{ $t('lcp.import_users.rows_uploaded') }}
            <v-icon color="error">mdi-close </v-icon>
            {{ getUsersWithErrors.length }} {{ $t('lcp.import_users.rows_have_errors') }}
            <v-divider class="my-4" />
          </v-col>
        </v-row>
        <v-row v-if="!importing" class="text-black">
          <v-col>
            <v-row>
              <v-col>
                <h3>{{ $t('lcp.import_users.import_via_the_tool') }}</h3>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <div class="text-subtitle-1">{{ $t('lcp.import_users.the_tool_description') }}</div>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-btn :href="downloadFiles.excelTemplate" color="primary" outlined class="my-4">
                  <v-icon left>mdi-download</v-icon>
                  <span class="text-none"> {{ $t('lcp.import_users.excel_template_for_upload') }}</span>
                </v-btn>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="text-subtitle-1">
                {{ $t('lcp.import_users.guides_about_the_tool') }}
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="auto">
            <a
              v-if="downloadFiles.excelGuideNative"
              :href="downloadFiles.excelGuideNative"
              target="_blank"
              rel="noopener noreferrer"
            >
              <v-icon left>mdi-file-document-outline</v-icon>
              <span class="text-none text-decoration-underline">
                {{ $t('lcp.import_users.guide_in_native_language') }}
              </span>
            </a>
          </v-col>
          <v-col>
            <a :href="downloadFiles.excelEnglishGuideNative" target="_blank" rel="noopener noreferrer">
              <v-icon left>mdi-file-document-outline</v-icon>
              <span class="text-none text-decoration-underline">
                {{ $t('lcp.import_users.guide_in_english_language') }}
              </span>
            </a>
          </v-col>
        </v-row>

        <v-row v-if="canRemoveStudents">
          <v-col>
            <h4>{{ $t('lcp.import_users.delete_students') }}</h4>
            <v-radio-group v-model="deleteStudents" mandatory row>
              <v-radio :label="$t('yes')" :value="true"></v-radio>
              <v-radio :label="$t('no')" :value="false"></v-radio>
            </v-radio-group>
            <v-divider />
          </v-col>
        </v-row>
        <v-row v-if="canRemoveEmployees">
          <v-col>
            <pt-4>{{ $t('lcp.import_users.delete_employees') }}</pt-4>
            <v-radio-group v-model="deleteEmployees" mandatory row>
              <v-radio :label="$t('yes')" :value="true"></v-radio>
              <v-radio :label="$t('no')" :value="false"></v-radio>
            </v-radio-group>
            <v-divider />
          </v-col>
        </v-row>
        <v-row v-if="canRemoveGuardians">
          <v-col>
            <div class="h4 primary--text pt-4">{{ $t('lcp.import_users.delete_guardians') }}</div>
            <v-radio-group v-model="deleteGuardians" mandatory row>
              <v-radio :label="$t('yes')" :value="true"></v-radio>
              <v-radio :label="$t('no')" :value="false"></v-radio>
            </v-radio-group>
            <v-divider />
          </v-col>
        </v-row>

        <v-row v-if="errorRows.length > 0" class="controlpanel-import">
          <v-col>
            <v-data-table :items="errorRows" :headers="importErrorHeaders" :items-per-page="-1" hide-default-footer>
              <template #top>
                <div class="h4 primary--text">
                  {{ $t('lcp.import_users.change_rows_and_import') }}
                </div>
              </template>
              <template #item.row="{ item }">
                <v-icon color="error" size="small">mdi-close</v-icon>
                {{ item.row + 1 }}
              </template>
              <template v-for="column in importErrorColumns" :key="column.field" #[column.slot]="{ item }">
                <span :class="{ 'text-error': getFieldError(item, column.field) }">{{
                  getFieldDisplay(item, column.field)
                }}</span>
              </template>
            </v-data-table>
          </v-col>
        </v-row>

        <v-row v-if="disallowedRoleRowCount > 0">
          <v-col>
            <v-alert
              type="warning"
              variant="outlined"
              border="start"
              :text="
                $t('lcp.import_users.disallowed_role_rows_warning', {
                  count: disallowedRoleRowCount,
                  roles: disallowedRoleWordsInFile.join(', '),
                  organizationType: organizationTypeLabel,
                })
              "
            ></v-alert>
          </v-col>
        </v-row>

        <v-row>
          <v-col :cols="true">
            <v-file-input
              prepend-icon="mdi-folder"
              accept=".xlsx"
              filled
              solo
              full-width
              :label="$t('lcp.import_users.choose_file')"
              @update:modelValue="handleFileChange"
            >
            </v-file-input>
          </v-col>
          <v-col v-if="validated" cols="auto">
            <v-btn v-model="file" color="primary" rounded dark x-large :disabled="!validated" @click="startImporting">
              <v-icon>mdi-upload</v-icon>
              <span class="text-none">{{ $t('lcp.import_users.start_import') }}</span>
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row class="mx-auto">
      <v-col cols="12" class="pa-md-0">
        <div class="controlpanel-import">
          <div v-if="!importing && excelRight"></div>
          <div v-if="!allDone">
            <h4>{{ progressText }}</h4>
            <v-progress-linear
              v-if="importing"
              :model-value="progress * 100"
              color="primary"
              class="text-white"
              dark
              height="25"
            >
              <template #default="{ value }">
                <strong v-if="value > 0">{{ Math.ceil(value) }}%</strong>
                <span v-else class="text-black"> {{ $t('preparing') }} </span>
              </template>
            </v-progress-linear>
          </div>
        </div>
      </v-col>
    </v-row>
    <v-row v-if="allDone && getUsersWithErrors.length > 0" class="controlpanel-import mx-auto">
      <v-col>
        <v-data-table
          :items="getUsersWithErrors"
          :headers="importErrorHeaders"
          :items-per-page="-1"
          hide-default-footer
        >
          <template #top>
            <div class="h4 primary--text">
              {{ $t('lcp.import_users.users_not_imported') }}
            </div>
            <div class="subtitle-1 secondary--text">
              {{ $t('lcp.import_users.change_rows_and_import') }}
            </div>
          </template>
          <template #item.row="{ item }">
            <v-icon color="error" size="small">mdi-close</v-icon>
            {{ item.row + 1 }}
            <span v-if="getRowLevelApiError(item)" class="text-error">— {{ getRowLevelApiError(item) }}</span>
          </template>
          <template v-for="column in importErrorColumns" :key="column.field" #[column.slot]="{ item }">
            <span :class="{ 'text-error': getFieldApiError(item, column.field) }">{{
              getFieldApiDisplay(item, column.field)
            }}</span>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<style lang="scss"></style>
